import { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import DateInput from '../../../components/DateInput/Index';
import { criarDataDeISO } from '../../../global/dataDigitada';
import { useSnackbar } from 'notistack';
import server from '../../../global/server';

// Junta a data escolhida no calendario com o horario digitado (HH:mm)
const montarPrazo = (data, hora) => {
    if (!data || !hora) return null;

    const [h, m] = String(hora).split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;

    const prazo = new Date(data);
    prazo.setHours(h, m, 0, 0);
    return prazo;
};

const paraHoraTexto = data => {
    const d = new Date(data);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export default ({ open, schedule, onClose, onSaved }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [data, setData] = useState(null);
    const [hora, setHora] = useState('');
    const [salvando, setSalvando] = useState(false);

    // Reabrir o modal sempre parte do que esta salvo no horario
    useEffect(() => {
        if (!open) return;
        if (schedule?.raffle_at) {
            setData(new Date(schedule.raffle_at));
            setHora(paraHoraTexto(schedule.raffle_at));
        } else {
            setData(null);
            setHora('');
        }
    }, [open, schedule?.id, schedule?.raffle_at]);

    const encontroEm = schedule?.datetime ? new Date(schedule.datetime) : null;

    const salvar = async () => {
        const prazo = montarPrazo(data, hora);
        if (!prazo) {
            enqueueSnackbar('Escolha a data e o horário limite', { variant: 'warning' });
            return;
        }
        if (prazo.getTime() <= Date.now()) {
            enqueueSnackbar('O prazo precisa ser no futuro', { variant: 'warning' });
            return;
        }
        if (encontroEm && prazo.getTime() > encontroEm.getTime()) {
            enqueueSnackbar('O prazo precisa ser antes do horário do encontro', { variant: 'warning' });
            return;
        }

        try {
            setSalvando(true);
            await server.patch(`/profile/schedule/${schedule.id}/raffle-config`, {
                raffle_at: prazo.toISOString(),
            });
            enqueueSnackbar('Prazo do sorteio salvo', { variant: 'success' });
            await onSaved?.();
            onClose();
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || 'Erro ao salvar o prazo', { variant: 'error' });
        } finally {
            setSalvando(false);
        }
    };

    const remover = async () => {
        try {
            setSalvando(true);
            await server.patch(`/profile/schedule/${schedule.id}/raffle-config`, { raffle_at: null });
            enqueueSnackbar('Prazo removido', { variant: 'success' });
            await onSaved?.();
            onClose();
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || 'Erro ao remover o prazo', { variant: 'error' });
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => !salvando && onClose()} fullWidth maxWidth="xs"
            PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontWeight: 600 }}>Configurações de sorteio</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Defina até quando você pretende sortear o conviva. Passando desse prazo
                        sem o sorteio manual, o sistema realiza o sorteio automaticamente.
                    </Typography>

                    <DateInput
                        label="Data do sorteio"
                        value={data}
                        onChange={iso => setData(iso ? criarDataDeISO(iso) : null)}
                        minima={new Date()}
                        maxima={encontroEm || undefined}
                        rotuloMinima="A data não pode ser no passado"
                        rotuloMaxima="A data precisa ser até o dia do encontro"
                    />

                    {/* O horario limite so faz sentido depois da data escolhida */}
                    <TextField
                        type="time"
                        label="Horário limite"
                        size="small"
                        fullWidth
                        disabled={!data}
                        value={hora}
                        onChange={e => setHora(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 60 }}
                        helperText={
                            data
                                ? 'Depois desse horário o sorteio é feito pelo sistema'
                                : 'Escolha a data para liberar o horário'
                        }
                    />

                    {encontroEm && (
                        <Alert severity="info" sx={{ borderRadius: '12px', '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
                            Sem prazo definido, o sorteio automático acontece 10 minutos antes do
                            encontro ({encontroEm.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}).
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                {Boolean(schedule?.raffle_at) && (
                    <Button onClick={remover} disabled={salvando} sx={{ textTransform: 'none', color: '#999', mr: 'auto' }}>
                        Remover prazo
                    </Button>
                )}
                <Button onClick={onClose} disabled={salvando} sx={{ textTransform: 'none', color: '#666' }}>
                    Cancelar
                </Button>
                <LoadingButton
                    onClick={salvar}
                    loading={salvando}
                    variant="contained"
                    sx={{ textTransform: 'none', bgcolor: '#006a61', '&:hover': { bgcolor: '#005a52' } }}
                >
                    Salvar
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};
